"use server";

import { headers } from "next/headers";
import { z } from "zod";

import { internationalPhoneSchema } from "@/shared/lib/phone/phone";
import { verifyTurnstile } from "@/shared/lib/turnstile/verifyTurnstile";

import type { ConsultationFormState } from "../model/types";

function errorState(
  prevState: ConsultationFormState,
  errors: ConsultationFormState["errors"],
): ConsultationFormState {
  return {
    success: false,
    errors,
    resetId: prevState.resetId,
    turnstileResetId: Date.now(),
  };
}

const schema = z.object({
  phone: internationalPhoneSchema,
  consent: z.boolean().refine((val) => val, {
    message: "Требуется согласие",
  }),
  marketing: z.boolean(),
});

export async function submitConsultationForm(
  prevState: ConsultationFormState,
  formData: FormData,
): Promise<ConsultationFormState> {
  const turnstileToken = formData.get("cf-turnstile-response");

  if (typeof turnstileToken !== "string" || !turnstileToken) {
    return errorState(prevState, {
      turnstile: ["Подтвердите, что вы не робот"],
    });
  }

  const headersList = await headers();
  const forwardedFor = headersList.get("x-forwarded-for");
  const remoteIp =
    forwardedFor?.split(",")[0]?.trim() ||
    headersList.get("x-real-ip") ||
    undefined;

  const isHuman = await verifyTurnstile(turnstileToken, remoteIp);

  if (!isHuman) {
    return errorState(prevState, {
      turnstile: ["Проверка не пройдена. Попробуйте ещё раз"],
    });
  }

  const rawData = {
    phone: formData.get("phone"),
    consent: formData.get("consent") === "on",
    marketing: formData.get("marketing") === "on",
  };

  const parsed = schema.safeParse(rawData);

  if (!parsed.success) {
    return errorState(prevState, parsed.error.flatten().fieldErrors);
  }

  try {
    const res = await fetch(`${process.env.STRAPI_URL}/api/consultations`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        data: parsed.data,
      }),
      cache: "no-store",
    });

    if (!res.ok) {
      throw new Error();
    }

    return { success: true, errors: {}, resetId: Date.now() };
  } catch {
    return errorState(prevState, {
      form: ["Ошибка отправки"],
    });
  }
}
